/**
 * Truy cập màn Phân công nhiệm vụ của module `van-ban` (`/api/v1/m/van-ban/task-assignments*`, ADR-0084):
 * danh sách phẳng kèm thống kê, thêm tay / sửa (multipart, kèm tệp), xoá, tải tệp đính kèm, AI phân công
 * từ tệp (tác vụ nền, tra tiến độ theo `job_id`) và từ văn bản đã tóm tắt. Chọn API thật hay dữ liệu mẫu
 * bằng `dungApiThat()` lúc gọi, như `van-ban.repository.ts`.
 */
import { dungApiThat, goiApi, goiApiTep } from '../api/http';
import type { ApiError } from '../errors';
import { MOCK_DON_VI } from '../../mock/van-ban';
import { MOCK_CAU_PHAN_CONG as CAU, MOCK_NHIEM_VU_PHAN_CONG } from '../../mock/van-ban-phan-cong';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  ChuTrichTuTep,
  GhiNhiemVuTay,
  LuotPhanCong,
  NhiemVuPhanCong,
  TienDoPhanCong,
  TrangPhanCong,
  YeuCauPhanCongTep,
} from '../../modules/xu-ly-van-ban/types-phan-cong';

const GOC = '/api/v1/m/van-ban';
const TA = `${GOC}/task-assignments`;
/** Màn đọc một lần tối đa 200 dòng rồi lọc ở client, như bản cũ. */
const MOT_TRANG = { page: 1, page_size: 200 };

const ma = (id: string) => encodeURIComponent(id);

/** Thân multipart chung của thêm tay và sửa; trường rỗng thì không gửi, như bản cũ. */
function bieuMauNhiemVu(vao: GhiNhiemVuTay): FormData {
  const fd = new FormData();
  fd.append('task_content', vao.task_content);
  fd.append('unit_id', vao.unit_id);
  if (vao.deadline) fd.append('deadline', vao.deadline);
  if (vao.related_document) fd.append('related_document', vao.related_document);
  if (vao.file) fd.append('file', vao.file, vao.file.name);
  return fd;
}

// ─── Bản giả ─────────────────────────────────────────────────────────────────

const khongThay = (message: string): ApiError => ({ code: 'NOT_FOUND', status: 404, message });
const tuChoi = <T>(loi: ApiError): Promise<T> => mockRequest<null>(null).then(() => Promise.reject(loi));
const bayGio = () => new Date().toISOString();
const idMoi = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mock-${Date.now()}`;

let nhiemVu: NhiemVuPhanCong[] = structuredClone(MOCK_NHIEM_VU_PHAN_CONG);
/** Lượt phân công từ tệp: xong sau hai lần tra tiến độ, để màn diễn được trạng thái chờ. */
const luot = new Map<string, { lanTra: number; tenTep: string; ketQua: NhiemVuPhanCong[] }>();

function ghiTay(cu: NhiemVuPhanCong | null, vao: GhiNhiemVuTay): NhiemVuPhanCong | ApiError {
  const dv = MOCK_DON_VI.find((d) => d.id === vao.unit_id);
  if (!dv) return khongThay(CAU.donViKhongCo);
  const tep = vao.file?.name ?? cu?.attachment_filename ?? null;
  return {
    ...(cu ?? {
      id: idMoi(),
      summarization_id: null,
      status: 'MOI_PHAN_CONG',
      status_label: CAU.trangThai.MOI_PHAN_CONG,
      created_at: bayGio(),
    }),
    task_content: vao.task_content,
    related_document: vao.related_document || cu?.related_document || null,
    summarization_name: cu?.summarization_id ? cu.summarization_name : vao.related_document || cu?.related_document || null,
    deadline: vao.deadline || cu?.deadline || null,
    attachment_filename: tep,
    unit_id: dv.id,
    unit_name: dv.name,
    unit_type: dv.type,
    updated_at: bayGio(),
  } as NhiemVuPhanCong;
}

function trangMock(): Promise<TrangPhanCong> {
  const ds = mockState() === 'filtered-empty' ? [] : nhiemVu;
  const dem = (t: NhiemVuPhanCong['unit_type']) => nhiemVu.filter((n) => n.unit_type === t).length;
  return mockRequest<TrangPhanCong>(
    {
      stats: {
        total: nhiemVu.length,
        phong_ban: dem('PHONG_BAN'),
        ca_nhan: dem('CA_NHAN'),
        completed: nhiemVu.filter((n) => n.status === 'HOAN_THANH').length,
      },
      data: [...ds].sort((a, b) => b.created_at.localeCompare(a.created_at)),
      total: ds.length,
      page: 1,
      page_size: 200,
      total_pages: 1,
    },
    {
      stats: { total: 0, phong_ban: 0, ca_nhan: 0, completed: 0 },
      data: [],
      total: 0,
      page: 1,
      page_size: 200,
      total_pages: 1,
    },
  );
}

/** Hai việc giả giao cho hai phòng ban đầu tiên của danh sách vai trò các cấp. */
function taoViecGia(tenVanBan: string, tep: string | null, summarizationId: string | null): NhiemVuPhanCong[] {
  return MOCK_DON_VI.filter((d) => d.type === 'PHONG_BAN')
    .slice(0, 2)
    .map((dv, i) => ({
      id: idMoi(),
      summarization_id: summarizationId,
      summarization_name: tenVanBan,
      task_content: `${CAU.viecTuTep} (${i + 1})`,
      related_document: tenVanBan,
      attachment_filename: tep,
      deadline: null,
      status: 'MOI_PHAN_CONG',
      status_label: CAU.trangThai.MOI_PHAN_CONG,
      unit_id: dv.id,
      unit_name: dv.name,
      unit_type: dv.type,
      created_at: bayGio(),
      updated_at: bayGio(),
    }));
}

const banGia = {
  danhSach: trangMock,
  chiTiet: (id: string): Promise<NhiemVuPhanCong> => {
    const n = nhiemVu.find((x) => x.id === id);
    return n ? mockRequest({ ...n }) : tuChoi(khongThay(CAU.khongThay));
  },
  tao: (vao: GhiNhiemVuTay): Promise<NhiemVuPhanCong> => {
    const moi = ghiTay(null, vao);
    if ('code' in moi) return tuChoi(moi);
    nhiemVu = [moi, ...nhiemVu];
    return mockRequest(moi);
  },
  sua: (id: string, vao: GhiNhiemVuTay): Promise<NhiemVuPhanCong> => {
    const cu = nhiemVu.find((x) => x.id === id);
    if (!cu) return tuChoi(khongThay(CAU.khongThay));
    const moi = ghiTay(cu, vao);
    if ('code' in moi) return tuChoi(moi);
    nhiemVu = nhiemVu.map((x) => (x.id === id ? moi : x));
    return mockRequest(moi);
  },
  xoa: (id: string): Promise<void> => {
    nhiemVu = nhiemVu.filter((x) => x.id !== id);
    return mockRequest(undefined);
  },
  taiTep: (id: string): Promise<Blob> => {
    const n = nhiemVu.find((x) => x.id === id);
    if (!n?.attachment_filename) return tuChoi(khongThay(CAU.khongCoTep));
    return mockRequest(new Blob([CAU.noiDungTep], { type: 'text/plain' }));
  },
  trichChu: (tep: File): Promise<ChuTrichTuTep> =>
    mockRequest({ text: CAU.trichDoan, filename: tep.name, char_count: CAU.trichDoan.length }),
  phanCongTuTep: (vao: YeuCauPhanCongTep): Promise<LuotPhanCong> => {
    const id = idMoi();
    luot.set(id, { lanTra: 0, tenTep: vao.file.name, ketQua: [] });
    return mockRequest({ job_id: id, status: 'queued', message: CAU.daTiepNhan });
  },
  tienDo: (jobId: string): Promise<TienDoPhanCong> => {
    const l = luot.get(jobId);
    if (!l) {
      return mockRequest({ job_id: jobId, status: 'not_found', done: true, created_count: null, tasks: null, message: null });
    }
    l.lanTra += 1;
    if (l.lanTra < 2) {
      return mockRequest({ job_id: jobId, status: 'in_progress', done: false, created_count: null, tasks: null, message: null });
    }
    if (!l.ketQua.length) {
      const ten = l.tenTep.replace(/\.[^.]+$/, '');
      l.ketQua = taoViecGia(ten, l.tenTep, null);
      nhiemVu = [...l.ketQua, ...nhiemVu];
    }
    const ten = l.tenTep.replace(/\.[^.]+$/, '');
    return mockRequest({
      job_id: jobId,
      status: 'complete',
      done: true,
      created_count: l.ketQua.length,
      tasks: l.ketQua,
      message: CAU.daTao(l.ketQua.length, ten),
    });
  },
};

// ─── Bản thật ────────────────────────────────────────────────────────────────

const banThat: typeof banGia = {
  danhSach: () => goiApi<TrangPhanCong>(`${TA}/flat`, { query: MOT_TRANG }),
  chiTiet: (id) => goiApi<NhiemVuPhanCong>(`${TA}/${ma(id)}`),
  tao: (vao) => goiApi<NhiemVuPhanCong>(TA, { method: 'POST', bieuMau: bieuMauNhiemVu(vao) }),
  sua: (id, vao) => goiApi<NhiemVuPhanCong>(`${TA}/${ma(id)}`, { method: 'PATCH', bieuMau: bieuMauNhiemVu(vao) }),
  xoa: (id) => goiApi<void>(`${TA}/${ma(id)}`, { method: 'DELETE' }),
  taiTep: (id) => goiApiTep(`${TA}/${ma(id)}/attachment`, '*/*'),
  trichChu: (tep) => {
    const fd = new FormData();
    fd.append('file', tep, tep.name);
    return goiApi<ChuTrichTuTep>(`${GOC}/compose/extract-text`, { method: 'POST', bieuMau: fd });
  },
  phanCongTuTep: (vao) => {
    const fd = new FormData();
    fd.append('file', vao.file, vao.file.name);
    fd.append('priority', vao.priority);
    fd.append('default_deadline_days', String(vao.default_deadline_days));
    if (vao.description) fd.append('description', vao.description);
    return goiApi<LuotPhanCong>(`${TA}/assign-from-file`, { method: 'POST', bieuMau: fd });
  },
  tienDo: (jobId) => goiApi<TienDoPhanCong>(`${TA}/jobs/${ma(jobId)}`),
};

const chon = () => (dungApiThat() ? banThat : banGia);

export const phanCongRepository = {
  danhSach: () => chon().danhSach(),
  chiTiet: (id: string) => chon().chiTiet(id),
  tao: (vao: GhiNhiemVuTay) => chon().tao(vao),
  sua: (id: string, vao: GhiNhiemVuTay) => chon().sua(id, vao),
  xoa: (id: string) => chon().xoa(id),
  taiTep: (id: string) => chon().taiTep(id),
  trichChu: (tep: File) => chon().trichChu(tep),
  phanCongTuTep: (vao: YeuCauPhanCongTep) => chon().phanCongTuTep(vao),
  tienDo: (jobId: string) => chon().tienDo(jobId),
  /** `tenVanBan` chỉ để bản mẫu đặt tên văn bản nguồn; API thật tự đọc. */
};
